/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// Disabling bitwise as it is useful in the case of routes
/* eslint-disable no-bitwise */

import { retry } from '@deckee/utilities';
import { lazy } from 'react';
import CoursesFeature from '../../components/features/Courses/CoursesFeature';
import DropPinFeature from '../../components/features/DropPin/DropPinFeature';
import IncidentReportFeature from '../../components/features/IncidentReport/IncidentReportFeature';
import InsuranceFeature from '../../components/features/Insurance/InsuranceFeature';
import MarkersFeature from '../../components/features/Markers/MarkersFeature';
import MarketplaceFeature from '../../components/features/Marketplace/MarketplaceFeature';
import MyLocationFeature from '../../components/features/MyLocation/MyLocationFeature';
import NotificationsFeature from '../../components/features/Notifications/NotificationsFeature';
import PrepareFeature from '../../components/features/Prepare/PrepareFeature';
import RegulationsFeature from '../../components/features/Regulations/RegulationsFeature';
import RemindersFeature from '../../components/features/Reminders/RemindersFeature';
import MapSettingsFeature from '../../components/features/MapSettings/MapSettingsFeature';
import SharingFeature from '../../components/features/Sharing/SharingFeature';
import SignalFeature from '../../components/features/Signal/SignalFeature';
import SignInFeature from '../../components/features/SignIn/SignInFeature';
import SubscriberFeature from '../../components/features/Subscriber/SubscriberFeature';
import RampGuidanceFeature from '../../components/features/RampGuidance/RampGuidanceFeature';
import SupportFeature from '../../components/features/Support/SupportFeature';
import VesselsFeature from '../../components/features/Vessels/VesselsFeature';
import PartnerFeature from '../../components/features/Partner/PartnerFeature';
import PlanFeature from '../../components/features/Plan/PlanFeature';
import { telemetry } from '../backend';
import NullComponent from './null-component';
import {
  ACCESS_AUTHENTICATED,
  ACCESS_NOT_AUTHENTICATED,
  authenticationCheck,
  MAP_URL,
  SIGN_UP_URL,
  START_URL,
  UNAUTHENTICATED_URL,
} from './permissions';
import {
  ROUTE_ABOUT_US,
  ROUTE_ACCOUNT_SETTINGS,
  ROUTE_ACCOUNT_SETTINGS_INTERESTS,
  ROUTE_ALERT_DETAILS,
  ROUTE_AUTHENTICATION_EMAIL_VALIDATION_OF_CLIENT_TOKEN,
  ROUTE_AUTHENTICATION_UNSUBSCRIBE_EMAIL,
  ROUTE_BOAT_DETAILS_CREATE,
  ROUTE_BOAT_DETAILS_EDIT,
  ROUTE_BOAT_DETAILS_VIEW,
  ROUTE_CATCH_ALL,
  ROUTE_COURSES,
  ROUTE_DISTANCE_TOOL,
  ROUTE_DROP_PIN,
  ROUTE_FLOAT_PLAN,
  ROUTE_FLOAT_PLAN_ACTIVATE,
  ROUTE_FLOAT_PLAN_END,
  ROUTE_FLOAT_PLAN_LIST,
  ROUTE_FLOAT_TRIP_VIEW,
  ROUTE_GET_NOTIFIED,
  ROUTE_RAMP_GUIDANCE,
  ROUTE_HELM,
  ROUTE_HELM_POSITION_OVERRIDE,
  ROUTE_INCIDENT_REPORT,
  ROUTE_INSURANCE,
  ROUTE_LOCATION_DISCLOSURE,
  ROUTE_LOCATION_SEARCH,
  ROUTE_LOGIN,
  ROUTE_MAP,
  ROUTE_MAP_SETTINGS,
  ROUTE_MARKERS,
  ROUTE_MARKER_COMMENT_READ,
  ROUTE_MARKER_EDIT,
  ROUTE_MARKER_FLAG,
  ROUTE_MARKER_READ,
  ROUTE_MARKER_SUMMARY,
  ROUTE_MARKER_SUMMARY_DETAILS,
  ROUTE_MARKETPLACE,
  ROUTE_MESSAGE,
  ROUTE_MY_BOATS,
  ROUTE_MY_LOCATION,
  ROUTE_MY_TRIPS_DETAILS,
  ROUTE_MY_TRIPS_EDIT,
  ROUTE_MY_TRIPS_SUMMARY,
  ROUTE_NEW_MARKER,
  ROUTE_NEW_MARKER_COMMENT,
  ROUTE_NEW_MARKER_SELECT_ENTITY_TYPE,
  ROUTE_NEW_MARKER_SET_LOCATION,
  ROUTE_NEW_STORY_MARKER,
  ROUTE_NOTIFICATIONS,
  ROUTE_ONBOARDING,
  ROUTE_ON_THE_WATER_PROMPT,
  ROUTE_PARTNER_FEATURE,
  ROUTE_PREPARE,
  ROUTE_REGULATIONS,
  ROUTE_REMINDERS,
  ROUTE_SETTINGS,
  ROUTE_SETTINGS_COMPLETE_PROFILE,
  ROUTE_SETTINGS_DELETE,
  ROUTE_SETTINGS_GENERAL,
  ROUTE_SETTINGS_LOCATION,
  ROUTE_SETTINGS_LOCATION_EDIT,
  ROUTE_SETTINGS_PROFILE,
  ROUTE_SHARING,
  ROUTE_SIGNAL,
  ROUTE_STORY_MARKER_EDIT,
  ROUTE_SUBSCRIBER,
  ROUTE_SUPPORT,
  ROUTE_TRIP_ACTIVE,
  ROUTE_TRIP_CREATE_PLAN,
  ROUTE_TRIP_LOG_OFF,
  ROUTE_TRIP_LOG_OFF_CONFIRMED,
  ROUTE_TRIP_LOG_ON,
  ROUTE_TRIP_LOG_ON_CONFIRMED,
  ROUTE_TRIP_RESUME,
  ROUTE_TRIP_SAVE,
  ROUTE_TRIP_SETTINGS,
  ROUTE_TRIP_UPDATE_PLAN,
  ROUTE_VESSELS,
  ROUTE_WEATHER_LOCATION_SEARCH,
  ROUTE_WEATHER_MOST_RECENT_POSITION,
  ROUTE_WEATHER_SETTINGS,
  ROUTE_WEATHER_SETTINGS_LAYOUT,
  ROUTE_WEATHER_SHOW,
  ROUTE_WEATHER_WARNING_DETAILS,
  ROUTE_YOU,
  ROUTE_PLAN,
} from './route-names';
import { initialize as initializeRouteNavigation } from './route-navigation';
import AppSettingsFeature from '../../components/features/AppSettings/AppSettingsFeature';

const EmailValidationOfClientToken = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "EmailValidationOfClientToken" */ /* webpackMode: "lazy" */ '../../components/pages/AuthenticationEmailValidationOfClientToken/AuthenticationEmailValidationOfClientToken'
    ),
  ),
);
const CatchAll = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "CatchAll" */ /* webpackMode: "lazy" */ '../../components/pages/CatchAll/CatchAll'
    ),
  ),
);
const LocationDisclosureModal = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "LocationDisclosureModal" */ /* webpackMode: "lazy" */ '../../components/pages/LocationDisclosureModal/LocationDisclosureModal'
    ),
  ),
);
const UnsubscribeFromEmail = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "UnsubscribeFromEmail" */ /* webpackMode: "lazy" */ '../../components/pages/UnsubscribeFromEmail/UnsubscribeFromEmail'
    ),
  ),
);
const ProfileSettings = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "ProfileSettings" */ /* webpackMode: "lazy" */ '../../components/containers/ProfileSettings/ProfileSettings'
    ),
  ),
);
const MarkerCreateSelectEntityType = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "MarkerCreateSelectEntityType" */ /* webpackMode: "lazy" */ '../../components/containers/MarkerCreateSelectEntityType/MarkerCreateSelectEntityType'
    ),
  ),
);

const CommentCreateContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "CommentCreateContainer" */ /* webpackMode: "lazy" */ '../../components/containers/CommentCreateContainer/CommentCreateContainer'
    ),
  ),
);
const MarkerCommentContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "MarkerCommentContainer" */ /* webpackMode: "lazy" */ '../../components/features/Markers/MarkerCommentContainer/MarkerCommentContainer'
    ),
  ),
);
const MarkerCreateContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "MarkerCreateContainer" */ /* webpackMode: "lazy" */ '../../components/containers/MarkerCreateContainer/MarkerCreateContainer'
    ),
  ),
);
const StoryMarkerCreateContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "StoryMarkerCreateContainer" */ /* webpackMode: "lazy" */ '../../components/containers/StoryMarkerCreateContainer/StoryMarkerCreateContainer'
    ),
  ),
);
const MarkerEditContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "MarkerEditContainer" */ /* webpackMode: "lazy" */ '../../components/containers/MarkerEditContainer/MarkerEditContainer'
    ),
  ),
);
const StoryMarkerEditContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "StoryMarkerEditContainer" */ /* webpackMode: "lazy" */ '../../components/containers/StoryMarkerEditContainer/StoryMarkerEditContainer'
    ),
  ),
);
const MarkerFlagContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "MarkerFlagContainer" */ /* webpackMode: "lazy" */ '../../components/containers/MarkerFlagContainer/MarkerFlagContainer'
    ),
  ),
);
const AlertDetailsContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "AlertDetailsContainer" */ /* webpackMode: "lazy" */ '../../components/features/Notifications/AlertDetailsContainer/AlertDetailsContainer'
    ),
  ),
);
const WeatherWarningDetailsContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "WeatherWarningDetailsContainer" */ /* webpackMode: "lazy" */ '../../components/features/weather/WeatherWarningDetailsContainer/WeatherWarningDetailsContainer'
    ),
  ),
);
const SettingsContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "SettingsContainer" */ /* webpackMode: "lazy" */ '../../components/containers/SettingsContainer/SettingsContainer'
    ),
  ),
);
const SettingsHelmContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "SettingsHelmContainer" */ /* webpackMode: "lazy" */ '../../components/containers/SettingsHelmContainer/SettingsHelmContainer'
    ),
  ),
);
const SettingsHelmOverridePositionContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "SettingsHelmOverridePositionContainer" */ /* webpackMode: "lazy" */ '../../components/containers/SettingsHelmOverridePositionContainer/SettingsHelmOverridePositionContainer'
    ),
  ),
);

const AboutUsContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "AboutUsContainer" */ /* webpackMode: "lazy" */ '../../components/containers/AboutUsContainer/AboutUsContainer'
    ),
  ),
);
const GetNotifiedContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "GetNotifiedContainer" */ /* webpackMode: "lazy" */ '../../components/containers/GetNotifiedContainer/GetNotifiedContainer'
    ),
  ),
);

const AccountSettings = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "AccountSettings" */ /* webpackMode: "lazy" */ '../../components/containers/AccountSettings/AccountSettings'
    ),
  ),
);
const ChooseInterests = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "ChooseInterests" */ /* webpackMode: "lazy" */ '../../components/containers/OnboardingContainer/ChooseInterests/ChooseInterests'
    ),
  ),
);
const BoatDetailsCreate = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "BoatDetailsCreate" */ /* webpackMode: "lazy" */ '../../components/containers/BoatDetailsCreate/BoatDetailsCreate'
    ),
  ),
);
const BoatDetailsEdit = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "BoatDetailsEdit" */ /* webpackMode: "lazy" */ '../../components/containers/BoatDetailsEdit/BoatDetailsEdit'
    ),
  ),
);
const BoatsContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "BoatsContainer" */ /* webpackMode: "lazy" */ '../../components/containers/BoatsContainer/BoatsContainer'
    ),
  ),
);
const BoatDetailsViewContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "BoatDetailsViewContainer" */ /* webpackMode: "lazy" */ '../../components/containers/BoatDetailsViewContainer/BoatDetailsViewContainer'
    ),
  ),
);
const HomeLocation = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "HomeLocation" */ /* webpackMode: "lazy" */ '../../components/containers/HomeLocation/HomeLocation'
    ),
  ),
);
const DeleteAccount = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "HomeLocation" */ /* webpackMode: "lazy" */ '../../components/containers/DeleteAccount/DeleteAccount'
    ),
  ),
);
const HomeLocationEdit = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "HomeLocationEdit" */ /* webpackMode: "lazy" */ '../../components/containers/HomeLocationEdit/HomeLocationEdit'
    ),
  ),
);
const CompleteProfileContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "CompleteProfileContainer" */ /* webpackMode: "lazy" */ '../../components/containers/CompleteProfileContainer/CompleteProfileContainer'
    ),
  ),
);

const ActiveTripPaneContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "ActiveTripPaneContainer" */ /* webpackMode: "lazy" */ '../../components/containers/ActiveTripPaneContainer/ActiveTripPaneContainer'
    ),
  ),
);
const TripDetailsContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripDetailsContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripDetailsContainer/TripDetailsContainer'
    ),
  ),
);
const TripSummaryContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripSummaryContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripSummaryContainer/TripSummaryContainer'
    ),
  ),
);
const TripSaveContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripSaveContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripSaveContainer/TripSaveContainer'
    ),
  ),
);
const TripDetailsEdit = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripDetailsEdit" */ /* webpackMode: "lazy" */ '../../components/containers/TripDetailsEdit/TripDetailsEdit'
    ),
  ),
);
const TripResumeContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripResumeContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripResumeContainer/TripResumeContainer'
    ),
  ),
);

const TripPlanCreateContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripPlanCreateContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripPlanCreateContainer/TripPlanCreateContainer'
    ),
  ),
);
const TripPlanEditContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripPlanEditContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripPlanEditContainer/TripPlanEditContainer'
    ),
  ),
);
const TripLogOnContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripLogOnContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripLogOnContainer/TripLogOnContainer'
    ),
  ),
);
const TripLogOnConfirmedContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripLogOnConfirmedContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripLogOnConfirmedContainer/TripLogOnConfirmedContainer'
    ),
  ),
);
const TripLogOffConfirmedContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripLogOffConfirmedContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripLogOffConfirmedContainer/TripLogOffConfirmedContainer'
    ),
  ),
);
const TripLogOffContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "TripLogOffContainer" */ /* webpackMode: "lazy" */ '../../components/containers/TripLogOffContainer/TripLogOffContainer'
    ),
  ),
);

const ActiveFloatPlanContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "ActiveFloatPlanContainer" */ /* webpackMode: "lazy" */ '../../components/containers/ActiveFloatPlanContainer/ActiveFloatPlanContainer'
    ),
  ),
);
const FloatPlanEndContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "FloatPlanEndContainer" */ /* webpackMode: "lazy" */ '../../components/containers/FloatPlanEndContainer/FloatPlanEndContainer'
    ),
  ),
);
const FloatPlanListContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "FloatPlanListContainer" */ /* webpackMode: "lazy" */ '../../components/containers/FloatPlanListContainer/FloatPlanListContainer'
    ),
  ),
);
const FloatPlanTripViewContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "FloatPlanTripViewContainer" */ /* webpackMode: "lazy" */ '../../components/containers/FloatPlanTripViewContainer/FloatPlanTripViewContainer'
    ),
  ),
);

const LocationSearchContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "LocationSearchContainer" */ /* webpackMode: "lazy" */ '../../components/containers/LocationSearchContainer/LocationSearchContainer'
    ),
  ),
);

const ActivateFloatPlanContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "ActivateFloatPlanContainer" */ /* webpackMode: "lazy" */ '../../components/features/FloatPlan/ActivateFloatPlanContainer/ActivateFloatPlanContainer'
    ),
  ),
);

const WeatherContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "WeatherContainer" */ /* webpackMode: "lazy" */ '../../components/features/weather/WeatherContainer/WeatherContainer'
    ),
  ),
);

const WeatherMostRecentContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "WeatherMostRecentContainer" */ /* webpackMode: "lazy" */ '../../components/containers/WeatherMostRecentContainer/WeatherMostRecentContainer'
    ),
  ),
);
const SettingsWeatherContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "SettingsWeatherContainer" */ /* webpackMode: "lazy" */ '../../components/features/weather/SettingsWeatherContainer/SettingsWeatherContainer'
    ),
  ),
);
const SettingsWeatherLayoutContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "SettingsWeatherLayoutContainer" */ /* webpackMode: "lazy" */ '../../components/features/weather/SettingsWeatherLayoutContainer/SettingsWeatherLayoutContainer'
    ),
  ),
);
const SettingsTripContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "SettingsTripContainer" */ /* webpackMode: "lazy" */ '../../components/containers/SettingsTripContainer/SettingsTripContainer'
    ),
  ),
);
const SettingsGeneralContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "SettingsGeneralContainer" */ /* webpackMode: "lazy" */ '../../components/containers/SettingsGeneralContainer/SettingsGeneralContainer'
    ),
  ),
);
const WeatherLocationSearchContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "WeatherLocationSearchContainer" */ /* webpackMode: "lazy" */ '../../components/containers/WeatherLocationSearchContainer/WeatherLocationSearchContainer'
    ),
  ),
);

const OnboardingContainer = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "OnboardingContainer" */ /* webpackMode: "lazy" */ '../../components/containers/OnboardingContainer/OnboardingContainer'
    ),
  ),
);

const YouFeature = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "YouFeature" */ /* webpackMode: "lazy" */ '../../components/features/You/YouFeature'
    ),
  ),
);

const NewMarkerSetLocation = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "NewMarkerSetLocation" */ /* webpackMode: "lazy" */ '../../components/containers/NewMarkerSetLocation/NewMarkerSetLocation'
    ),
  ),
);
const DistanceTool = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "DistanceTool" */ /* webpackMode: "lazy" */ '../../components/null-components/DistanceTool/DistanceTool'
    ),
  ),
);
const Map = lazy(() =>
  retry(() =>
    import(
      /* webpackChunkName: "Map" */ /* webpackMode: "lazy" */ '../../components/null-components/Map/Map'
    ),
  ),
);

export const routesConfiguration = [
  // In the case that we want to feature a route we can easily do so with the hasFeatureEnabled
  // function like below. We filter all falesy values out of the array so this method works.
  // !hasFeatureEnabled('featureName') ? null : [
  //   ROUTE_SLIDE_UP_MENU,
  //   `${MAP_URL}/edit`,
  //   NullComponent,
  //   ACCESS_AUTHENTICATED,
  //   {/* STATIC_PROPS_FOR_ROUTE */ },
  //   {/* ROUTE_CONFIGURATION */ },
  // ],
  [
    ROUTE_NEW_MARKER_SET_LOCATION,
    `${MAP_URL}/marker/new/set-location`,
    NewMarkerSetLocation,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_NEW_MARKER_SELECT_ENTITY_TYPE,
    `${MAP_URL}/marker/new/select-type/:lat/:lon`,
    MarkerCreateSelectEntityType,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_NEW_STORY_MARKER,
    `${MAP_URL}/marker/new/storyMarker/:lat/:lon`,
    StoryMarkerCreateContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_NEW_MARKER,
    `${MAP_URL}/marker/new/:markerType/:lat/:lon`,
    MarkerCreateContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_STORY_MARKER_EDIT,
    `${MAP_URL}/marker/edit/storyMarker/:id`, // edit
    StoryMarkerEditContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_MARKER_EDIT,
    `${MAP_URL}/marker/edit/:markerType/:id`, // edit
    MarkerEditContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_SUPPORT,
    `${MAP_URL}/support`,
    SupportFeature,
    ACCESS_AUTHENTICATED,
    {},
    {},
  ],
  [
    ROUTE_MARKERS,
    `${MAP_URL}/marker/:pagename(view|summary)`, // custom static marker details page
    MarkersFeature,
    ACCESS_AUTHENTICATED,
    {},
    {},
  ],
  // TODO: Anything that navigates to this URL still uses this configuration, but the actual rendering has
  // been moved into the MarkerFeature
  [
    ROUTE_MARKER_SUMMARY_DETAILS,
    `${MAP_URL}/marker/summary/details/:url`, // custom static marker details page
    NullComponent,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  // TODO: Anything that navigates to this URL still uses this configuration, but the actual rendering has
  // been moved into the MarkerFeature
  [
    ROUTE_MARKER_SUMMARY,
    `${MAP_URL}/marker/summary/:markerType/:id`, // marker popover
    NullComponent,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // TODO: Anything that navigates to this URL still uses this configuration, but the actual rendering has
  // been moved into the MarkerFeature
  [
    ROUTE_MARKER_READ,
    `${MAP_URL}/marker/view/:markerType/:id`, // marker view
    NullComponent,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_MARKER_FLAG,
    `${MAP_URL}/marker/flag/:markerType/:id`, // report a problem
    MarkerFlagContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_MARKER_COMMENT_READ, // view comments
    `${MAP_URL}/marker/comment/view/:markerType/:id`,
    MarkerCommentContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_NEW_MARKER_COMMENT,
    `${MAP_URL}/marker/comment/new/:markerType/:id`,
    CommentCreateContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // ////////////////////////////////////////////////////////////////////////////////////////
  // ///////////////////////////////////////////////////////////////////////////////////////
  // LOLO and trip routes
  [
    ROUTE_TRIP_LOG_OFF_CONFIRMED,
    `${MAP_URL}/trips/log-off-confirmed`,
    TripLogOffConfirmedContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_TRIP_LOG_ON_CONFIRMED,
    `${MAP_URL}/trips/log-on-confirmed`,
    TripLogOnConfirmedContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_TRIP_LOG_ON,
    `${MAP_URL}/trips/log-on`,
    TripLogOnContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_TRIP_LOG_OFF,
    `${MAP_URL}/trips/log-off`,
    TripLogOffContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_TRIP_UPDATE_PLAN,
    `${MAP_URL}/trips/plan/update/:tripPlanID`,
    TripPlanEditContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_TRIP_CREATE_PLAN,
    `${MAP_URL}/trips/plan/create`,
    TripPlanCreateContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // Trip Routes
  [
    ROUTE_MY_TRIPS_EDIT,
    `${MAP_URL}/trips/edit/:id`,
    TripDetailsEdit,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_MY_TRIPS_DETAILS,
    `${MAP_URL}/trips/summary/details/:id`, // trip details view (notes)
    TripDetailsContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_MY_TRIPS_SUMMARY,
    `${MAP_URL}/trips/summary/:id`, // trip details view
    TripSummaryContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_TRIP_SAVE,
    `${MAP_URL}/trips/save/:id`, // trip details view
    TripSaveContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_TRIP_RESUME,
    `${MAP_URL}/trips/resume`,
    TripResumeContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_TRIP_ACTIVE,
    `${MAP_URL}/trips/active`,
    ActiveTripPaneContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_TRIP_SETTINGS,
    `${MAP_URL}/trips/settings`,
    SettingsTripContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  // ///////////////////////////////////////////////////////////////////////////////////////

  // Float Plan Routes
  [
    ROUTE_FLOAT_PLAN_ACTIVATE,
    `${MAP_URL}/float-plan/activate/:type`,
    ActivateFloatPlanContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_FLOAT_PLAN_END,
    `${MAP_URL}/float-plan/end`,
    FloatPlanEndContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
      replaceHistory: true,
    },
  ],
  [
    ROUTE_FLOAT_TRIP_VIEW,
    `${MAP_URL}/float-plan/trip/:token`,
    FloatPlanTripViewContainer,
    ACCESS_AUTHENTICATED | ACCESS_NOT_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_FLOAT_PLAN,
    `${MAP_URL}/float-plan/:token`,
    ActiveFloatPlanContainer,
    ACCESS_AUTHENTICATED | ACCESS_NOT_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_FLOAT_PLAN_LIST,
    `${MAP_URL}/float-plans`,
    FloatPlanListContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // Weather Routes
  [
    ROUTE_WEATHER_LOCATION_SEARCH,
    `${MAP_URL}/weather/location/search/:latitude/:longitude/:weatherType?`,
    WeatherLocationSearchContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_WEATHER_SETTINGS_LAYOUT,
    `${MAP_URL}/weather/settings/layout`,
    SettingsWeatherLayoutContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_WEATHER_SETTINGS,
    `${MAP_URL}/weather/settings`,
    SettingsWeatherContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_WEATHER_WARNING_DETAILS,
    `${MAP_URL}/weather/warning/:id`,
    WeatherWarningDetailsContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_WEATHER_SHOW,
    `${MAP_URL}/weather/:latitude/:longitude`,
    WeatherContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    `${ROUTE_WEATHER_MOST_RECENT_POSITION}HOME`,
    `${MAP_URL}/weather`,
    WeatherMostRecentContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_WEATHER_MOST_RECENT_POSITION,
    `${MAP_URL}/weather/recent`,
    WeatherMostRecentContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // My location
  [
    ROUTE_MY_LOCATION,
    `${MAP_URL}/my-location`,
    MyLocationFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],

  [
    ROUTE_PREPARE,
    `${MAP_URL}/prepare`,
    PrepareFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_VESSELS,
    `${MAP_URL}/vessels`,
    VesselsFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_LOCATION_DISCLOSURE, // TODO: move to route MyLocation feature
    `${MAP_URL}/location-disclosure`,
    LocationDisclosureModal,
    ACCESS_AUTHENTICATED,
    {
      title: 'Use your location',
      message:
        'To log and save your trips on the water, allow Deckee to use your location in the background.',
      nextRoute: ROUTE_TRIP_ACTIVE,
      startTracking: false,
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_ON_THE_WATER_PROMPT, // TODO: move to route MyLocation feature
    `${MAP_URL}/on-the-water-permissions`,
    LocationDisclosureModal,
    ACCESS_AUTHENTICATED,
    {
      title: 'Automatic logbook',
      message:
        'DECKEE uses your location in the background to share your Signal with safety contacts and automatically save your activities on the water',
      startTracking: true,
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      nextRoute: ROUTE_MAP,
      /* ROUTE_CONFIGURATION */
    },
  ],

  // Location search
  [
    ROUTE_LOCATION_SEARCH,
    `${MAP_URL}/location/search`,
    LocationSearchContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // Settings Routes
  [
    ROUTE_HELM_POSITION_OVERRIDE,
    `${MAP_URL}/settings/helm/override-position`,
    SettingsHelmOverridePositionContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_HELM,
    `${MAP_URL}/settings/helm`,
    SettingsHelmContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_PARTNER_FEATURE,
    `${MAP_URL}/partner`,
    PartnerFeature,
    ACCESS_AUTHENTICATED,
    {},
    {},
  ],
  // Setup duplicate so we can attempt to redirect old links
  [
    `${ROUTE_PARTNER_FEATURE}_legacy`,
    `${MAP_URL}/safety-information`,
    PartnerFeature,
    ACCESS_AUTHENTICATED,
    {},
    {},
  ],
  [
    ROUTE_SETTINGS_PROFILE,
    `${MAP_URL}/settings/profile`,
    ProfileSettings,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // Utility / feature routes
  [
    ROUTE_SETTINGS_GENERAL,
    `${MAP_URL}/settings/general`,
    SettingsGeneralContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_GET_NOTIFIED,
    `${MAP_URL}/region/get-notified`,
    GetNotifiedContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_ABOUT_US,
    `${MAP_URL}/settings/about`,
    AboutUsContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_SETTINGS_COMPLETE_PROFILE,
    `${MAP_URL}/settings/account/complete-profile`,
    CompleteProfileContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_SETTINGS_LOCATION_EDIT,
    `${MAP_URL}/settings/account/location/edit`,
    HomeLocationEdit,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_SETTINGS_LOCATION,
    `${MAP_URL}/settings/account/location`,
    HomeLocation,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_SETTINGS_DELETE,
    `${MAP_URL}/settings/account/delete`,
    DeleteAccount,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_BOAT_DETAILS_EDIT,
    `${MAP_URL}/settings/account/boats/edit/:id`,
    BoatDetailsEdit,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_BOAT_DETAILS_CREATE,
    `${MAP_URL}/settings/account/boats/new`,
    BoatDetailsCreate,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_MY_BOATS,
    `${MAP_URL}/settings/account/boats`,
    BoatsContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_BOAT_DETAILS_VIEW,
    `${MAP_URL}/boats/:id`,
    BoatDetailsViewContainer,
    ACCESS_AUTHENTICATED | ACCESS_NOT_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_ACCOUNT_SETTINGS_INTERESTS,
    `${MAP_URL}/settings/account/interests`,
    ChooseInterests,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_ACCOUNT_SETTINGS,
    `${MAP_URL}/settings/account/`,
    AccountSettings,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_MAP_SETTINGS,
    `${MAP_URL}/settings/map`,
    MapSettingsFeature,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // NOTE: All app settings page should be moved into this feature, including the  `SettingsContainer`
  [
    'APP_SETTINGS',
    `${MAP_URL}/settings/:page`,
    AppSettingsFeature,
    ACCESS_AUTHENTICATED,
    {},
    {},
  ],
  [
    ROUTE_SETTINGS,
    `${MAP_URL}/settings`,
    SettingsContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  [
    ROUTE_REMINDERS,
    `${MAP_URL}/reminders`,
    RemindersFeature,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      exact: false,
    },
  ],

  [
    ROUTE_DISTANCE_TOOL,
    `${MAP_URL}/distance`,
    DistanceTool,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // authentication
  [
    ROUTE_AUTHENTICATION_EMAIL_VALIDATION_OF_CLIENT_TOKEN,
    '/authentication/evoct',
    EmailValidationOfClientToken,
    ACCESS_AUTHENTICATED | ACCESS_NOT_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_AUTHENTICATION_UNSUBSCRIBE_EMAIL,
    '/authentication/unsubscribe',
    UnsubscribeFromEmail,
    ACCESS_AUTHENTICATED | ACCESS_NOT_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_ALERT_DETAILS,
    `${MAP_URL}/alerts/details/:id`,
    AlertDetailsContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],

  // This is used to show the default popover messages
  [
    ROUTE_MESSAGE,
    `${MAP_URL}/message`,
    NullComponent,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_YOU,
    `${MAP_URL}/you`,
    YouFeature,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
  [
    ROUTE_MAP,
    MAP_URL,
    Map,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    { exact: true },
  ],
  [
    ROUTE_ONBOARDING,
    SIGN_UP_URL,
    OnboardingContainer,
    ACCESS_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    { exact: true },
  ],
  [
    ROUTE_INCIDENT_REPORT,
    `${MAP_URL}/incident-report`,
    IncidentReportFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_COURSES,
    `${MAP_URL}/courses`,
    CoursesFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_REGULATIONS,
    `${MAP_URL}/regulations/:latitude/:longitude`,
    RegulationsFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_DROP_PIN,
    `${MAP_URL}/drop-pin`,
    DropPinFeature,
    ACCESS_AUTHENTICATED | ACCESS_NOT_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_INSURANCE,
    `${MAP_URL}/insurance`,
    InsuranceFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_MARKETPLACE,
    `${MAP_URL}/marketplace`,
    MarketplaceFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      expect: false,
    },
  ],
  [
    ROUTE_SIGNAL,
    `${MAP_URL}/signal`,
    SignalFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_NOTIFICATIONS,
    `${MAP_URL}/notifications`,
    NotificationsFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_SHARING,
    `${MAP_URL}/share`,
    SharingFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],

  [
    ROUTE_RAMP_GUIDANCE,
    `${MAP_URL}/ramp-guidance`,
    RampGuidanceFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_PLAN,
    `${MAP_URL}/plan`,
    PlanFeature,
    ACCESS_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_SUBSCRIBER,
    `${MAP_URL}/subscriber`,
    SubscriberFeature,
    ACCESS_AUTHENTICATED | ACCESS_NOT_AUTHENTICATED,
    {},
    {
      exact: false,
    },
  ],
  [
    ROUTE_LOGIN,
    UNAUTHENTICATED_URL,
    SignInFeature,
    ACCESS_NOT_AUTHENTICATED,
    {
      /* STATIC_PROPS_FOR_ROUTE */
    },
    {
      /* ROUTE_CONFIGURATION */
      exact: true,
    },
  ],

  // four04, this should not be redirected to. It should be the base component when no routes
  // are hit.
  /**
   * We seem to be relying on this route to redirect the user to login when it is required.
   * DON'T DO THIS, this means authentication is implicit - we want to control this more directly.
   */
  [
    ROUTE_CATCH_ALL,
    '/',
    CatchAll,
    ACCESS_AUTHENTICATED,
    {
      UNAUTHENTICATED_URL,
      ACCESS_NOT_AUTHENTICATED,
      ACCESS_AUTHENTICATED,
      START_URL,
      authenticationCheck,
    },
    {
      /* ROUTE_CONFIGURATION */
    },
  ],
];

export const initialize = ({ history, initialRoute }) => {
  // Initialize this services sub service used by components for navigation.
  initializeRouteNavigation({ history, routesConfiguration, initialRoute });

  const initialConfiguration = routesConfiguration;
  const routeNames = new Set();
  const allIssues = initialConfiguration
    .map((configuration) => {
      const issues = [];
      const routeName = configuration[0];
      if (routeNames.has(routeName)) {
        issues.push(
          `Route name ${routeName} is used more than once. Please ensure that names are unique for all routes.`,
        );
      }
      routeNames.add(routeName);

      // A route needs to look like the following
      // [
      //   ROUTE_NAME,
      //   ROUTE_PATH,
      //   ROUTE_COMPONENT,
      //   AUTHENTICATION,
      //   STATIC_PROPS_FOR_ROUTE,
      //   ROUTE_CONFIGURATION,
      // ]

      const types = ['string', 'string', 'any', 'number', 'object', 'object'];

      types.forEach((type, index) => {
        if (!configuration[index]) issues.push(`Item ${index} is falesy`);
        /* eslint-disable-next-line valid-typeof */
        if (type !== 'any' && typeof configuration[index] !== type)
          issues.push(`Item ${index} not a ${type}`);
      });

      if (issues.length) {
        return [configuration, ...issues];
      }
      return null;
    })
    .filter((i) => i);

  if (allIssues.length) {
    telemetry(
      telemetry.ERROR,
      `[routeService] routeConfiguration invalid, found the following issues: ${JSON.stringify(
        allIssues,
      )}`,
    );
  }
};
